import React, { useMemo, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Table } from 'design-system-web';
import { AdminPage } from 'components';
import { H3 } from 'components/typography';

import type { InspectionsChannel } from '../types/channel';
import { useGetInspectionTemplates } from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';
import styles from './InspectionsTemplateLibrary.scss';
import {
  type TemplateLibraryTableRow,
  mapTemplateToTableRow,
  getTemplateLibraryColumns,
  getTemplateLibraryRowActions,
} from './templateLibraryTableConfig';

type Props = {
  channel: InspectionsChannel;
};

export function InspectionsTemplateLibrary({ channel }: Props) {
  const { t } = useTranslation();
  const { templates, loading, error, fetchInspectionTemplates, called } =
    useGetInspectionTemplates();

  useEffect(() => {
    if (!channel?._id) {
      return;
    }
    const request = fetchInspectionTemplates({
      buildingId: channel._id,
      entityType: 'building',
      sourceType: SourceType.SourceTypeActivate,
    });
    // This screen relies on hook state for error UI; attach a handler so the effect does not leave a rejected promise.
    if (request) {
      request.catch(() => undefined);
    }
  }, [channel?._id, fetchInspectionTemplates]);

  const rows = useMemo(
    () => templates.map(mapTemplateToTableRow),
    [templates]
  );

  const columns = useMemo(() => getTemplateLibraryColumns(t), [t]);

  const rowActions = useMemo(() => getTemplateLibraryRowActions(t), [t]);

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.templateLibrary}>
          <div className={styles.header}>
            <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
          </div>
          <p>{t('web.admin.inspections.templateLibrary.error')}</p>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.templateLibrary}>
        <div className={styles.header}>
          <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
        </div>
        <Table<TemplateLibraryTableRow>
          columns={columns}
          data={rows}
          isLoading={loading || !called}
          pagination="client"
          defaultPageSize={25}
          rowActions={rowActions}
          rowActionsLabel={t(
            'web.admin.inspections.templateLibrary.actions.label'
          )}
          emptyMessage={t(
            'web.admin.inspections.templateLibrary.emptyState'
          )}
        />
      </div>
    </AdminPage>
  );
}
